/**
 * Application shell.
 *
 * Owns the stores, the router and the shared actions, then hands a
 * context object to each view. Views are plain render functions - no
 * framework, no virtual DOM, which keeps the bundle small enough for a
 * mid-range Android phone.
 */

import { appStore, VIEWS } from '../core/store.js';
import { DEFAULT_SETTINGS, RESOLUTIONS, RESEARCH } from '../core/config.js';
import { createSceneStore } from '../scenes/scene-store.js';
import { createPromptLibrary } from '../prompts/prompt-library.js';
import { createAudioEngine, computeWaveform, isLikelyAudioFile } from '../audio/audio-engine.js';
import { createRenderEngine } from '../video/render-engine.js';
import { createProviderRegistry } from '../providers/registry.js';
import { selectScene } from '../scenes/selection.js';
import { assessImport } from '../video/validation.js';
import { selectRecordingFormat } from '../video/codec.js';
import { planLoop, describePlan } from '../video/loop-engine.js';
import { loadSettings, saveSettings } from '../storage/settings-store.js';
import * as assets from '../storage/asset-store.js';
import * as history from '../storage/history-store.js';
import { storage, getStorageDiagnostics, isUsingFallback } from '../storage/db.js';
import { STORAGE } from '../core/config.js';
import { h, qs, downloadBlob, copyText } from '../utils/dom.js';
import { formatBytes, formatDuration, formatClock } from '../utils/format.js';
import { createRng, seedFromString } from '../utils/rng.js';
import { createLogger } from '../utils/log.js';
import { toastOk, toastWarn, toastError } from './components/toast.js';
import { openSheet, confirmSheet } from './components/sheet.js';
import { kv, note } from './components/common.js';

import { renderStudio } from './views/studio.js';
import { renderScenes } from './views/scenes.js';
import { renderGenerate } from './views/generate.js';
import { renderHistory } from './views/history.js';
import { renderSettings } from './views/settings.js';

const log = createLogger('app');

const VIEW_RENDERERS = {
  studio: renderStudio,
  scenes: renderScenes,
  generate: renderGenerate,
  history: renderHistory,
  settings: renderSettings,
};

export async function createApp() {
  const viewEl = qs('#view');
  const navEl = qs('#nav');
  const headerStatus = qs('#header-status');
  const canvas = qs('#render-canvas');
  const audioInput = qs('#audio-input');
  const videoInput = qs('#video-input');

  const sceneStore = createSceneStore();
  const promptLibrary = createPromptLibrary();
  const audioEngine = createAudioEngine();
  const renderEngine = createRenderEngine({ audioEngine });
  let settings = loadSettings();

  const registry = createProviderRegistry({
    getWorkerUrl: () => settings.workerUrl,
    assetStore: assets,
  });

  /** Pending import target, set when the operator starts an import. */
  let pendingImportSceneId = null;
  let pendingImportProviderId = 'local-library';
  let lastRenderResult = null;

  /**
   * Teardown callbacks for the currently mounted view.
   *
   * Views create animation loops (scene previews, IntersectionObservers).
   * Replacing the DOM does NOT stop those loops, so without this registry
   * every re-render leaks a requestAnimationFrame loop that keeps drawing
   * procedural scenes forever. During a render the view re-renders once a
   * second, which turned a 3 minute export into hundreds of leaked loops.
   */
  let viewDisposers = [];

  const ctx = {
    store: appStore,
    sceneStore,
    promptLibrary,
    audioEngine,
    renderEngine,
    registry,
    get settings() {
      return settings;
    },
    setSetting,
    actions: {},
    refresh: () => renderView(appStore.get().view),
    /** Register a teardown callback for the view currently being built. */
    onDispose(fn) {
      if (typeof fn === 'function') viewDisposers.push(fn);
      return fn;
    },
    view: 'studio',
  };

  /* ------------------------------------------------------------ helpers */

  function setSetting(patch, { silent = false } = {}) {
    settings = saveSettings(patch);
    appStore.set({ settings });
    if (!silent) renderView(appStore.get().view);
    return settings;
  }

  async function loadLibrary() {
    const [sceneOverrides, userPrompts, historyEntries, assetList] = await Promise.all([
      storage.getAll(STORAGE.stores.scenes),
      storage.getAll(STORAGE.stores.prompts),
      history.listHistory(),
      assets.listAssets(),
    ]);
    if (sceneOverrides.length) sceneStore.mergeOverrides(sceneOverrides);
    if (userPrompts.length) promptLibrary.mergeUserPrompts(userPrompts);
    appStore.set({ library: sceneStore.all(), prompts: promptLibrary.all(), history: historyEntries });
    return { assetList, historyEntries };
  }

  function persistScene(scene) {
    return storage.put(STORAGE.stores.scenes, {
      id: scene.id,
      enabled: scene.enabled,
      loopQuality: scene.loopQuality,
      weight: scene.weight,
      usageCount: scene.usageCount,
      lastUsed: scene.lastUsed,
      assetType: scene.assetType,
      assetId: scene.assetId ?? null,
      assetUrl: scene.assetUrl ?? null,
      duration: scene.duration,
      provider: scene.provider ?? null,
      assetMeta: scene.assetMeta ?? null,
      name: scene.name,
      description: scene.description,
    });
  }

  /** Auto-select a scene using the transparent weighted engine. */
  function autoSelectScene({ mood = null, mode = null, seed = null } = {}) {
    const selectedMode = mode ?? settings.sceneMode ?? 'auto';
    const rng = seed ? createRng(seedFromString(seed)) : createRng(Date.now() >>> 0);
    const current = appStore.get().selectedScene;
    const result = selectScene(sceneStore.all(), {
      mode: selectedMode === 'manual' ? 'auto' : selectedMode,
      mood: mood ?? settings.mood,
      rng,
      lastSceneId: current?.id ?? null,
    });
    if (result.scene) {
      appStore.set({ selectedScene: result.scene });
      log.info('scene selected', { id: result.scene.id, mode: result.mode });
    }
    return result;
  }

  function selectSceneById(id) {
    const scene = sceneStore.get(id);
    if (!scene) return null;
    appStore.set({ selectedScene: scene });
    return scene;
  }

  async function handleAudioFile(file) {
    if (!file) return null;
    if (!isLikelyAudioFile(file)) {
      toastError(`"${file.name}" does not look like an audio file. Use MP3, WAV, M4A, AAC or OGG.`);
      return null;
    }
    try {
      appStore.set({ busy: true });
      const decoded = await audioEngine.load(file);
      const peaks = computeWaveform(decoded.buffer, 96);
      const audio = {
        name: file.name,
        size: file.size,
        type: file.type,
        duration: decoded.duration,
        sampleRate: decoded.sampleRate,
        channels: decoded.channels,
        peaks,
      };
      appStore.set({ audio });
      log.info('audio loaded', { name: file.name, duration: decoded.duration });

      // First upload: make sure something is selected so the operator is
      // never staring at an empty preview.
      if (!appStore.get().selectedScene) autoSelectScene();
      renderView(appStore.get().view);
      toastOk(`Loaded ${file.name} - ${formatDuration(decoded.duration)}.`);
      return audio;
    } catch (error) {
      log.error('audio decode failed', error);
      toastError(`Could not decode that audio: ${error.message}`);
      return null;
    } finally {
      appStore.set({ busy: false });
    }
  }

  /**
   * Full import pipeline (spec sections 15 step 10-11, 32):
   * probe -> validate -> show review sheet -> store -> attach to scene.
   */
  async function handleVideoFile(file, { sceneId = null, providerId = 'local-library' } = {}) {
    const targetSceneId = sceneId ?? pendingImportSceneId;
    const provider = providerId ?? pendingImportProviderId;
    const scene = sceneStore.get(targetSceneId);
    if (!scene) {
      toastError('Select a scene before importing a clip.');
      return null;
    }
    if (!file) return null;

    appStore.set({ busy: true });
    try {
      const assessment = await assessImport(file, { targetAspectRatio: settings.aspectRatio });
      const rows = [
        ['File', file.name],
        ['Size', formatBytes(file.size)],
        ['Duration', assessment.probe.duration ? formatDuration(assessment.probe.duration) : 'could not be read'],
        ['Resolution', assessment.probe.width ? `${assessment.probe.width} x ${assessment.probe.height}` : 'unknown'],
        ['Target', `${settings.aspectRatio} at ${settings.resolution}`],
      ];

      const issues = assessment.issues.length
        ? h(
            'div',
            { class: 'stack' },
            assessment.issues.map((item) =>
              note(item.message, item.level === 'error' ? 'danger' : item.level === 'warning' ? 'warn' : 'info'),
            ),
          )
        : note('No problems detected.', 'ok');

      const decision = await openSheet({
        title: assessment.ok ? 'Review import' : 'Import blocked',
        body: h('div', { class: 'stack' }, [
          kv(rows),
          issues,
          note(
            'Loop quality is never assumed. After importing you mark it Seamless, Repeatable or Unknown.',
            'info',
          ),
        ]),
        actions: assessment.ok
          ? [
              { label: 'Cancel', value: 'cancel', kind: 'ghost' },
              { label: 'Import to library', value: 'import', kind: 'primary' },
            ]
          : [{ label: 'Close', value: 'cancel', kind: 'ghost' }],
      });

      if (decision !== 'import') return null;

      const record = assets.toAssetRecord({
        file,
        sceneId: scene.id,
        providerId: provider,
        probe: assessment.probe,
        loopQuality: 'unknown',
        meta: {
          validationLevel: assessment.level,
          issues: assessment.issues.map((i) => i.code),
        },
      });
      await assets.saveAsset(record);
      const blobUrl = await assets.getAssetUrl(record.id);
      const updated = sceneStore.attachAsset(scene.id, {
        assetId: record.id,
        blobUrl,
        durationSeconds: assessment.probe.duration ?? scene.duration,
        loopQuality: 'unknown',
        providerId: provider,
        meta: { width: assessment.probe.width, height: assessment.probe.height, bytes: record.size },
      });
      persistScene(updated);
      appStore.set({ selectedScene: updated, library: sceneStore.all() });

      await history.addHistoryEntry(
        history.createHistoryEntry({
          kind: provider === 'google-vids' ? 'manual-generation' : 'import',
          providerId: provider,
          sceneId: scene.id,
          sceneName: scene.name,
          status: 'COMPLETED',
          assetId: record.id,
          durationSeconds: assessment.probe.duration,
          aspectRatio: assessment.probe.width
            ? `${assessment.probe.width}:${assessment.probe.height}`
            : settings.aspectRatio,
          notes: `Imported ${file.name}`,
        }),
      );

      await loadLibrary();
      renderView(appStore.get().view);
      toastOk(`Imported into "${scene.name}". Now mark the loop quality.`);
      await promptLoopQuality(scene.id);
      return record;
    } catch (error) {
      log.error('import failed', error);
      toastError(`Import failed: ${error.message}`);
      return null;
    } finally {
      appStore.set({ busy: false });
    }
  }

  /** Loop quality is an operator decision, never inferred (spec section 16). */
  async function promptLoopQuality(sceneId) {
    const scene = sceneStore.get(sceneId);
    if (!scene) return null;
    const choice = await openSheet({
      title: 'Loop quality',
      body: h('div', { class: 'stack' }, [
        note(
          'Repeating a file does not make it seamless. Only mark Seamless if the first and last frame actually match.',
          'info',
        ),
      ]),
      actions: [
        { label: 'Unknown', value: 'unknown', kind: 'ghost' },
        { label: 'Repeatable', value: 'repeatable', kind: 'ghost' },
        { label: 'Seamless', value: 'seamless', kind: 'primary' },
      ],
    });
    if (!choice) return null;
    const updated = sceneStore.setLoopQuality(sceneId, choice);
    persistScene(updated);
    appStore.set({ library: sceneStore.all() });
    renderView(appStore.get().view);
    return choice;
  }

  /* ------------------------------------------------------------ render */

  function buildCurrentPlan() {
    const { audio, selectedScene } = appStore.get();
    if (!audio || !selectedScene) return null;
    const sourceKind = selectedScene.assetType === 'video' && selectedScene.assetUrl ? 'video' : 'procedural';
    const loopDuration = Math.max(1, Number(selectedScene.duration) || 10);
    return planLoop({
      audioDuration: audio.duration,
      loopDuration,
      mode: settings.loopMode,
      crossfadeSeconds: settings.crossfadeSeconds,
      pingPongSupported: sourceKind === 'procedural',
    });
  }

  function applyResolution() {
    const resolution = RESOLUTIONS.find((r) => r.id === settings.resolution) ?? RESOLUTIONS[0];
    canvas.width = resolution.width;
    canvas.height = resolution.height;
    return resolution;
  }

  /**
   * Update the render progress widgets in place.
   * @returns {boolean} false when the widgets are not on screen yet, which
   *   tells the caller to fall back to a full re-render.
   */
  function patchRenderProgress(update) {
    const bar = document.getElementById('render-bar');
    if (!bar) return false;
    bar.style.width = `${Math.round(Math.min(1, Math.max(0, update.progress ?? 0)) * 100)}%`;
    const phase = document.getElementById('render-phase');
    if (phase) phase.textContent = `Rendering - ${update.phase}`;
    const clock = document.getElementById('render-clock');
    if (clock) {
      clock.textContent = `${formatClock(update.elapsedSeconds ?? 0)} / ${formatClock(update.totalSeconds ?? 0)}`;
    }
    return true;
  }

  async function startRender() {
    const { audio, selectedScene } = appStore.get();
    if (!audio) {
      toastWarn('Upload an audio clip first.');
      return null;
    }
    if (!selectedScene) {
      toastWarn('Select a scene first.');
      return null;
    }

    const format = selectRecordingFormat();
    const plan = buildCurrentPlan();
    const resolution = RESOLUTIONS.find((r) => r.id === settings.resolution) ?? RESOLUTIONS[0];
    const hasAsset = Boolean(selectedScene.assetUrl) || selectedScene.assetType === 'procedural';

    const confirm = await openSheet({
      title: 'Create BEX CAST',
      body: h('div', { class: 'stack' }, [
        kv([
          ['Scene', selectedScene.name],
          ['Loop', `${selectedScene.duration}s - ${describePlan(plan)}`],
          ['Audio', `${formatDuration(audio.duration)}`],
          ['Output', `${resolution.width} x ${resolution.height}, ${settings.fps} fps`],
          ['Container', format.chosen ? format.chosen.label : 'unavailable'],
          ['Wall clock', `about ${Math.ceil(audio.duration)}s (real-time capture)`],
        ]),
        hasAsset
          ? note(
              'Recording runs in real time, so it takes about as long as the audio. Keep this tab in the foreground.',
              'info',
            )
          : note('This scene has no asset yet. Generate a loop first, or pick another scene.', 'warn'),
        format.warning ? note(format.warning, 'warn') : null,
      ]),
      actions: hasAsset
        ? [
            { label: 'Cancel', value: 'cancel', kind: 'ghost' },
            { label: 'Start render', value: 'start', kind: 'primary' },
          ]
        : [
            { label: 'Cancel', value: 'cancel', kind: 'ghost' },
            { label: 'Open Generate', value: 'generate', kind: 'primary' },
          ],
    });

    if (confirm === 'generate') {
      setView('generate');
      return null;
    }
    if (confirm !== 'start') return null;

    applyResolution();
    appStore.set({
      render: { status: 'preparing', progress: 0, phase: 'Preparing', elapsedSeconds: 0, totalSeconds: audio.duration, result: null, error: null },
    });
    renderView(appStore.get().view);

    try {
      const result = await renderEngine.render({
        canvas,
        scene: selectedScene,
        settings,
        analysis: audioEngine.analysis,
        allowFallback: false,
        onProgress: (update) => {
          appStore.set({
            render: {
              ...appStore.get().render,
              status: update.phase === 'done' ? 'done' : 'rendering',
              progress: update.progress ?? 0,
              phase: update.phase,
              elapsedSeconds: update.elapsedSeconds ?? 0,
              totalSeconds: update.totalSeconds ?? audio.duration,
              result: update.result ?? null,
              error: update.error ?? null,
            },
          });
          // Patch the progress UI in place. Re-rendering the whole view once
          // per second during a real-time capture costs main-thread time the
          // recorder needs, and used to leak one preview loop per tick.
          const terminal = update.phase === 'done' || update.phase === 'error' || update.phase === 'cancelled';
          if (terminal || !patchRenderProgress(update)) renderView(appStore.get().view);
        },
      });

      lastRenderResult = result;
      appStore.set({
        render: { status: 'done', progress: 1, phase: 'done', elapsedSeconds: result.durationSeconds, totalSeconds: result.durationSeconds, result, error: null },
      });

      sceneStore.recordUsage(selectedScene.id);
      const used = sceneStore.get(selectedScene.id);
      persistScene(used);
      appStore.set({ selectedScene: used });
      await history.addHistoryEntry(
        history.createHistoryEntry({
          kind: 'episode-render',
          providerId: 'local-renderer',
          sceneId: selectedScene.id,
          sceneName: selectedScene.name,
          status: 'COMPLETED',
          durationSeconds: result.durationSeconds,
          aspectRatio: settings.aspectRatio,
          resolution: `${result.width}x${result.height}`,
          notes: `${result.container.toUpperCase()} - ${formatBytes(result.bytes)}`,
        }),
      );
      await loadLibrary();
      renderView(appStore.get().view);
      toastOk(`Rendered ${result.container.toUpperCase()} - ${formatBytes(result.bytes)}.`);
      return result;
    } catch (error) {
      if (error?.name === 'AbortError') {
        toastWarn('Render cancelled.');
      } else {
        toastError(`Render failed: ${error.message}`);
      }
      appStore.set({
        render: { ...appStore.get().render, status: 'error', phase: 'error', error: error.message },
      });
      renderView(appStore.get().view);
      return null;
    }
  }

  function cancelRender() {
    renderEngine.cancel('cancelled by the operator');
  }

  function exportResult() {
    const result = lastRenderResult ?? appStore.get().render.result;
    if (!result?.blob) {
      toastWarn('Nothing to export yet.');
      return;
    }
    downloadBlob(result.blob, result.filename);
    toastOk(`Saved ${result.filename} (${result.container.toUpperCase()}).`);
  }

  async function shareResult() {
    const result = lastRenderResult ?? appStore.get().render.result;
    if (!result?.blob) return;
    const file = new File([result.blob], result.filename, { type: result.mimeType });
    if (navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: 'BEX CAST' });
        return;
      } catch {
        /* user cancelled - fall through to download */
      }
    }
    exportResult();
  }

  /* ------------------------------------------------------------ scenes */

  function triggerImport(sceneId, providerId = 'local-library') {
    pendingImportSceneId = sceneId;
    pendingImportProviderId = providerId;
    videoInput.value = '';
    videoInput.click();
  }

  function triggerAudioPick() {
    audioInput.value = '';
    audioInput.click();
  }

  /* ------------------------------------------------------------ routing */

  function setView(name) {
    if (!VIEWS.includes(name)) return;
    appStore.set({ view: name });
    renderView(name);
  }

  function renderView(name) {
    ctx.view = name;
    const renderer = VIEW_RENDERERS[name] ?? VIEW_RENDERERS.studio;

    // Hand the registry over to the new view, then tear the old one down.
    // Building first means a throwing view cannot leave a blank screen, and
    // swapping the registry first means we dispose the *previous* view's
    // loops rather than the ones we just created.
    const previous = viewDisposers;
    viewDisposers = [];
    let next;
    try {
      next = renderer(ctx);
    } catch (error) {
      viewDisposers = previous;
      throw error;
    }
    for (const dispose of previous) {
      try {
        dispose();
      } catch (error) {
        log.warn('view disposer failed', error);
      }
    }

    viewEl.replaceChildren(next);
    for (const item of navEl.querySelectorAll('.nav__item')) {
      item.setAttribute('aria-current', String(item.dataset.view === name));
    }
    window.scrollTo({ top: 0, behavior: 'instant' in window ? 'instant' : 'auto' });
  }

  function updateHeaderStatus() {
    const diagnostics = getStorageDiagnostics();
    const format = selectRecordingFormat();
    const parts = [
      isUsingFallback() ? 'memory storage' : 'indexeddb',
      format.container ? format.container.toUpperCase() : 'no recorder',
    ];
    if (headerStatus) {
      headerStatus.textContent = parts.join(' \u00b7 ');
      headerStatus.className = `chip ${format.container === 'mp4' ? 'chip--ok' : format.container ? 'chip--warn' : 'chip--danger'}`;
      headerStatus.title = [
        `Storage: ${diagnostics.driver}`,
        `Recording: ${format.chosen?.label ?? 'unavailable'}`,
        `Scenes: ${sceneStore.size()}`,
        `Research verified: ${RESEARCH.checkedOn}`,
      ].join('\n');
    }
  }

  /* ------------------------------------------------------------ wiring */

  navEl.addEventListener('click', (event) => {
    const item = event.target.closest('.nav__item');
    if (item) setView(item.dataset.view);
  });

  audioInput.addEventListener('change', async (event) => {
    const file = event.target.files?.[0];
    if (file) await handleAudioFile(file);
  });

  videoInput.addEventListener('change', async (event) => {
    const file = event.target.files?.[0];
    if (file) await handleVideoFile(file, { sceneId: pendingImportSceneId, providerId: pendingImportProviderId });
  });

  /* ------------------------------------------------------------ actions */

  Object.assign(ctx.actions, {
    setView,
    setSetting,
    render: renderView,
    autoSelectScene,
    selectSceneById,
    handleAudioFile,
    handleVideoFile,
    promptLoopQuality,
    triggerImport,
    triggerAudioPick,
    startRender,
    cancelRender,
    exportResult,
    shareResult,
    applyResolution,
    buildCurrentPlan,
    persistScene,
    loadLibrary,
    openSheet,
    confirmSheet,
    copyText,
    async openVidsForScene(sceneId) {
      const scene = sceneStore.get(sceneId);
      if (!scene) return;
      const provider = registry.get('google-vids');
      const promptEntry = promptLibrary.preferredForScene(scene.id, 'google-vids');
      const handoff = await provider.generate({
        scene,
        promptEntry,
        mood: settings.mood,
        style: promptEntry?.style,
        durationSeconds: Number(promptEntry?.duration ?? scene.duration) || 8,
        aspectRatio: settings.aspectRatio,
        resolution: null,
        loopMode: settings.loopMode,
      });
      appStore.set({ generate: { scene, promptEntry, handoff } });
      setView('generate');
    },
    async runAutomatedGeneration({ scene, promptEntry, prompt, model, durationSeconds, aspectRatio, resolution }) {
      const provider = registry.get('google-veo');
      const cost = provider.estimateGenerationCost({ model, resolution, durationSeconds });
      const existing = history.findSimilarGenerations(appStore.get().history, {
        signature: history.createHistoryEntry({ providerId: 'google-veo', sceneId: scene.id, prompt, durationSeconds, aspectRatio, resolution, model }).signature,
        prompt,
      });
      const duplicateWarning =
        existing.exact.length || existing.near.length
          ? note(
              existing.exact.length
                ? 'An identical generation request already exists in history. Generating again will be billed again.'
                : `A very similar prompt was already used (${existing.near[0].score * 100}% match). Generating again will be billed again.`,
              'warn',
            )
          : null;

      const confirm = await openSheet({
        title: 'Confirm paid generation',
        body: h('div', { class: 'stack' }, [
          kv([
            ['Provider', 'Google Veo (Gemini API)'],
            ['Model', model],
            ['Scene', scene.name],
            ['Duration', `${durationSeconds} seconds`],
            ['Aspect ratio', aspectRatio],
            ['Resolution', resolution],
            ['Estimated cost', cost ? `$${cost.totalUsd.toFixed(3)} USD (${cost.pricePerSecond}/s)` : 'unknown'],
            ['Quota', 'Consumes your Gemini API quota. No free tier is published for Veo.'],
          ]),
          duplicateWarning,
          note(
            'Nothing has been sent yet. This confirmation is mandatory before any paid request (spec section 28).',
            'info',
          ),
        ]),
        actions: [
          { label: 'Cancel', value: 'cancel', kind: 'ghost' },
          { label: 'Generate (billable)', value: 'go', kind: 'primary' },
        ],
      });
      if (confirm !== 'go') return null;

      const entry = history.createHistoryEntry({
        kind: 'automated-generation',
        providerId: 'google-veo',
        sceneId: scene.id,
        sceneName: scene.name,
        prompt,
        model,
        durationSeconds: Number(durationSeconds),
        aspectRatio,
        resolution,
        status: 'QUEUED',
        estimatedCostUsd: cost?.totalUsd ?? null,
      });
      await history.addHistoryEntry(entry);
      await loadLibrary();

      try {
        const operation = await provider.generate({
          prompt,
          negativePrompt: promptEntry?.negativePrompt ?? null,
          sceneId: scene.id,
          model,
          durationSeconds,
          aspectRatio,
          resolution,
        });
        await history.updateHistoryEntry(entry.id, { status: 'PROCESSING', notes: operation.operationName });
        await loadLibrary();
        renderView('generate');
        toastOk('Generation started. Polling the provider...');

        const status = await provider.waitForCompletion(operation.operationName, {
          onTick: () => {
            /* progress is surfaced by the Generate view polling state */
          },
        });
        if (status.error) throw new Error(status.error);
        if (!status.videoUri) throw new Error('The provider finished without returning a video URI.');

        const download = await provider.downloadResult(status.videoUri);
        const file = new File([download.blob], `${scene.id}-veo.mp4`, { type: download.mimeType });
        const assessment = await assessImport(file, { targetAspectRatio: settings.aspectRatio });
        const record = assets.toAssetRecord({
          file,
          sceneId: scene.id,
          providerId: 'google-veo',
          probe: assessment.probe,
          loopQuality: 'unknown',
          meta: { model, prompt },
        });
        await assets.saveAsset(record);
        const blobUrl = await assets.getAssetUrl(record.id);
        const updated = sceneStore.attachAsset(scene.id, {
          assetId: record.id,
          blobUrl,
          durationSeconds: assessment.probe.duration ?? Number(durationSeconds),
          loopQuality: 'unknown',
          providerId: 'google-veo',
        });
        persistScene(updated);
        await history.updateHistoryEntry(entry.id, {
          status: 'COMPLETED',
          assetId: record.id,
          durationSeconds: assessment.probe.duration,
        });
        appStore.set({ selectedScene: updated, library: sceneStore.all() });
        await loadLibrary();
        renderView('generate');
        toastOk('Veo clip downloaded and added to the Scene Library.');
        await promptLoopQuality(scene.id);
        return record;
      } catch (error) {
        log.error('veo generation failed', error);
        await history.updateHistoryEntry(entry.id, { status: 'FAILED', error: error.message });
        await loadLibrary();
        renderView('generate');
        toastError(`Generation failed: ${error.message}`);
        return null;
      }
    },
    async saveGeneratedAssetFromBlob(blob, { sceneId, providerId, name, meta }) {
      const file = new File([blob], name, { type: blob.type });
      const assessment = await assessImport(file, { targetAspectRatio: settings.aspectRatio });
      const record = assets.toAssetRecord({ file, sceneId, providerId, probe: assessment.probe, loopQuality: 'unknown', meta });
      await assets.saveAsset(record);
      const blobUrl = await assets.getAssetUrl(record.id);
      const updated = sceneStore.attachAsset(sceneId, {
        assetId: record.id,
        blobUrl,
        durationSeconds: assessment.probe.duration ?? null,
        loopQuality: 'unknown',
        providerId,
      });
      persistScene(updated);
      await loadLibrary();
      return { record, updated, assessment };
    },
    async deleteSceneAsset(sceneId) {
      const scene = sceneStore.get(sceneId);
      if (!scene?.assetId) return false;
      await assets.deleteAsset(scene.assetId);
      const updated = sceneStore.update(sceneId, {
        assetType: scene.proceduralId ? 'procedural' : 'none',
        assetId: null,
        assetUrl: null,
        loopQuality: 'unknown',
      });
      persistScene(updated);
      await loadLibrary();
      renderView(appStore.get().view);
      toastOk('Asset removed from the scene.');
      return true;
    },
    async refreshProviderStatus() {
      const described = await registry.describeAll();
      const map = {};
      for (const item of described) map[item.id] = item;
      appStore.set({ providerStatus: map });
      return described;
    },
    async clearAllData() {
      const confirmed = await confirmSheet({
        title: 'Delete all local data?',
        message:
          'This removes imported clips, scene overrides, prompts, history and settings from this browser. It cannot be undone.',
        confirmLabel: 'Delete everything',
        kind: 'danger',
      });
      if (!confirmed) return false;
      await Promise.all([
        assets.clearAssets(),
        storage.clear(STORAGE.stores.scenes),
        storage.clear(STORAGE.stores.prompts),
        storage.clear(STORAGE.stores.history),
        storage.clear(STORAGE.stores.settings),
      ]);
      settings = saveSettings(DEFAULT_SETTINGS);
      appStore.set({ history: [], library: sceneStore.all() });
      await loadLibrary();
      renderView(appStore.get().view);
      toastOk('All local data removed.');
      return true;
    },
  });

  /* ------------------------------------------------------------ boot */

  appStore.set({ settings, library: sceneStore.all(), prompts: promptLibrary.all() });
  await loadLibrary();

  // First run: pick something immediately so the Studio is never empty.
  if (!appStore.get().selectedScene) autoSelectScene({ mode: 'auto' });

  updateHeaderStatus();
  renderView('studio');

  // Refresh the header chip when storage or recorder support changes.
  window.addEventListener('online', updateHeaderStatus);
  window.addEventListener('offline', updateHeaderStatus);

  log.info('app ready', {
    scenes: sceneStore.size(),
    prompts: promptLibrary.size(),
    storage: getStorageDiagnostics(),
  });

  return { ctx, setView, setSetting, updateHeaderStatus };
}
